import { isEmpty } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { GlMasksResponse } from 'src/services/useGetGlMasks';
import { GLCodesBudgetByYearResponseObject } from 'src/services/useGlCodesBudgetByYearApi';
import { generateRegexPattern } from 'src/utils/constants';
import { convertToTwoDigitNumber, flattenMessages } from '.';

export interface CheckbookLineItemError {
  glCode: string[];
  glCodeFormat: ReactNode[];
  description: string[];
}

export interface CheckbookLineItemWarning {
  generic: string[];
  description: string[];
  isFixed: string[];
  isActive: string[];
  initialBudgetPeriod01: string[];
  initialBudgetPeriod02: string[];
  initialBudgetPeriod03: string[];
  initialBudgetPeriod04: string[];
  initialBudgetPeriod05: string[];
  initialBudgetPeriod06: string[];
  initialBudgetPeriod07: string[];
  initialBudgetPeriod08: string[];
  initialBudgetPeriod09: string[];
  initialBudgetPeriod10: string[];
  initialBudgetPeriod11: string[];
  initialBudgetPeriod12: string[];
  budgetPeriod01: string[];
  budgetPeriod02: string[];
  budgetPeriod03: string[];
  budgetPeriod04: string[];
  budgetPeriod05: string[];
  budgetPeriod06: string[];
  budgetPeriod07: string[];
  budgetPeriod08: string[];
  budgetPeriod09: string[];
  budgetPeriod10: string[];
  budgetPeriod11: string[];
  budgetPeriod12: string[];
  warnings: string[];
}
export interface CheckbookLineItemInfo {
  description: string[];
}

const defaultViewBudgetErrors: CheckbookLineItemError = {
  glCode: [],
  glCodeFormat: [],
  description: [],
};

const defaultViewBudgetWarnings: CheckbookLineItemWarning = {
  generic: [],
  description: [],
  isFixed: [],
  isActive: [],
  initialBudgetPeriod01: [],
  initialBudgetPeriod02: [],
  initialBudgetPeriod03: [],
  initialBudgetPeriod04: [],
  initialBudgetPeriod05: [],
  initialBudgetPeriod06: [],
  initialBudgetPeriod07: [],
  initialBudgetPeriod08: [],
  initialBudgetPeriod09: [],
  initialBudgetPeriod10: [],
  initialBudgetPeriod11: [],
  initialBudgetPeriod12: [],
  budgetPeriod01: [],
  budgetPeriod02: [],
  budgetPeriod03: [],
  budgetPeriod04: [],
  budgetPeriod05: [],
  budgetPeriod06: [],
  budgetPeriod07: [],
  budgetPeriod08: [],
  budgetPeriod09: [],
  budgetPeriod10: [],
  budgetPeriod11: [],
  budgetPeriod12: [],
  warnings: [],
};

export const defaultViewBudgetInfos: CheckbookLineItemInfo = {
  description: [],
};

const useCheckbookYearErrors = (
  items: GLCodesBudgetByYearResponseObject[],
  originalItems: GLCodesBudgetByYearResponseObject[],
  allItems: GLCodesBudgetByYearResponseObject[],
  isEditMode: boolean,
  regexPatternBE: string[],
  glFormatsResponse: GlMasksResponse
) => {
  const errors = useMemo(() => {
    const newlyAddedItems = [...items].filter((item) => item.isNewGlCode);
    return items?.map((item) => {
      let error: CheckbookLineItemError = { ...defaultViewBudgetErrors };
      const duplicateGLCodePeriod = [...allItems, ...newlyAddedItems]?.some(
        (glCode) =>
          item.code &&
          glCode?.id !== item.id &&
          glCode?.code?.toLowerCase().trim() === item?.code?.toLowerCase().trim()
      );
      if (isEditMode) {
        if (
          item?.isNewGlCode &&
          glFormatsResponse?.glMasks?.length &&
          glFormatsResponse?.setGlMask
        ) {
          const regex = generateRegexPattern(regexPatternBE, item.code);
          if (!regex) {
            const acceptableFormats = regexPatternBE?.map((pattern: string, index: number) => (
              <li key={index} className="whitespace-pre-wrap mb-1 list-item list-disc ml-4">
                {pattern}
              </li>
            ));
            const html = (
              <div>
                Invalid GL Code format. Allowed format(s):
                <ul>{acceptableFormats}</ul>
              </div>
            );

            error.glCodeFormat = [html];
          }
        }
        if (item?.isNewGlCode && duplicateGLCodePeriod) {
          error.glCode = ['GL Code must not be a duplicate'];
        }
        if (isEmpty(item?.code)) {
          error.glCode = ['GL Code must contain a value'];
        }
        if (item?.code?.length > 765) {
          error.glCode = ['GL Code is more than 765 characters'];
        }
        if (
          item?.isNewGlCode &&
          glFormatsResponse.glMasks.length === 0 &&
          glFormatsResponse?.setGlMask
        ) {
          error.glCode = ['No format specified. Contact corporate administrator to add format(s).'];
        }
        if (isEmpty(item?.description)) {
          error.description = ['Description must contain a value'];
        } else if (item.description?.length > 4000) {
          error.description = ['Description is more than 4000 characters'];
        }
      }
      return error;
    }, []);
  }, [
    allItems,
    glFormatsResponse?.glMasks,
    glFormatsResponse?.setGlMask,
    isEditMode,
    items,
    regexPatternBE,
  ]);

  const warnings = useMemo(
    () =>
      items.map((item) => {
        let warning: CheckbookLineItemWarning = { ...defaultViewBudgetWarnings };
        const originalGLCodeByYear = originalItems.find((glCode) => glCode.rowId === item.rowId);
        if (isEditMode) {
          if (!item.isNewGlCode) {
            if (item.description !== originalGLCodeByYear?.description) {
              warning.description = ['Description has unsaved changes'];
            }
            if (item.isActive !== originalGLCodeByYear?.isActive) {
              warning.isActive = ['Active Code has unsaved changes'];
            }
            if (item.isFixed !== originalGLCodeByYear?.isFixed) {
              warning.isFixed = ['Fixed Code has unsaved changes'];
            }
            [...new Array(item.periodBudgets?.length)].forEach((_, i) => {
              const period = 'initialBudgetPeriod' + convertToTwoDigitNumber(i + 1);
              if ((item as any)?.[period] !== (originalGLCodeByYear as any)?.[period]) {
                (warning as any)[period] = ['Budget Amount has unsaved changes'];
              }
            });
            [...new Array(item.periodBudgets?.length)].forEach((_, i) => {
              const period = 'budgetPeriod' + convertToTwoDigitNumber(i + 1);
              if ((item as any)?.[period] !== (originalGLCodeByYear as any)?.[period]) {
                (warning as any)[period] = ['Forecast Amount has unsaved changes'];
              }
            });
          } else {
            warning.generic = ['Newly added GL code'];
            warning.description = ['Description has unsaved changes'];
            warning.isActive = ['Active Code has unsaved changes'];
            warning.isFixed = ['Fixed Code has unsaved changes'];
            [...new Array(item.periodBudgets?.length)].forEach((_, i) => {
              const period = 'initialBudgetPeriod' + convertToTwoDigitNumber(i + 1);
              (warning as any)[period] = ['Budget Amount has unsaved changes'];
            });
            [...new Array(item.periodBudgets?.length)].forEach((_, i) => {
              const period = 'budgetPeriod' + convertToTwoDigitNumber(i + 1);
              (warning as any)[period] = ['Forecast Amount has unsaved changes'];
            });
          }
        }
        warning.warnings = [...Object.values(warning).flat()];
        return warning;
      }),
    [isEditMode, items, originalItems]
  );

  const infos = useMemo(
    () =>
      items.map((item) => {
        let messages: CheckbookLineItemInfo = { ...defaultViewBudgetInfos };

        return messages;
      }),
    [items]
  );
  const yearErrorList: string[] = flattenMessages(errors);
  const yearWarningList: string[] = flattenMessages(warnings);
  const yearInformationList: string[] = flattenMessages(infos);

  const hasUnsavedYearChanges = useMemo(
    () => yearWarningList.some((item) => item.toLowerCase().includes('unsaved')),
    [yearWarningList]
  );

  const hasYearErrors = useMemo(() => !!yearErrorList.length, [yearErrorList.length]);
  const hasYearWarnings = useMemo(() => !!yearWarningList.length, [yearWarningList.length]);

  return {
    errors,
    warnings,
    infos,
    yearErrorList,
    yearInformationList,
    yearWarningList,
    hasYearErrors,
    hasYearWarnings,
    hasUnsavedYearChanges,
  };
};

export default useCheckbookYearErrors;
